import { useMemo } from 'react';

import { IconCheckCircleFilled } from '@iwoca/icons';
import classNames from 'classnames';
import _ from 'lodash';
import { FormattedMessage } from 'react-intl';

import styles from './ApplicationProgressBar.module.css';

type TStepId =
    | 'applicationSubmitted'
    | 'documentsReview'
    | 'creditAnalystReview'
    | 'yourDecision';

type TStep = {
    id: TStepId;
    status: 'inactive' | 'active' | 'completed';
};

const applicationSteps: TStep[] = [
    {
        id: 'applicationSubmitted',
        status: 'inactive'
    },
    {
        id: 'documentsReview',
        status: 'inactive'
    },
    {
        id: 'creditAnalystReview',
        status: 'inactive'
    },
    {
        id: 'yourDecision',
        status: 'inactive'
    }
];

export function updateActiveSteps(stepId: TStepId) {
    const stepIndex = applicationSteps.findIndex((step) => {
        return step.id === stepId;
    });

    return applicationSteps.map((step: TStep, index: number) => {
        if (index < stepIndex) {
            step.status = 'completed';
        }
        if (index === stepIndex) {
            step.status = 'active';
        }

        return step;
    });
}

const ApplicationProgressBar = ({ appStep }: { appStep: TStepId }) => {
    const steps = useMemo(() => {
        return updateActiveSteps(appStep);
    }, [appStep]);

    const renderAppStep = (id: string) => {
        switch (id) {
            case 'applicationSubmitted':
                return (
                    <FormattedMessage
                        id="_.application_progress_bar.application_submitted"
                        defaultMessage="Application submitted"
                    />
                );
            case 'documentsReview':
                return (
                    <FormattedMessage
                        id="_.application_progress_bar.documents_review"
                        defaultMessage="Documents review"
                    />
                );
            case 'creditAnalystReview':
                return (
                    <FormattedMessage
                        id="_.application_progress_bar.credit_analyst_review"
                        defaultMessage="Credit analyst review"
                    />
                );
            case 'yourDecision':
                return (
                    <FormattedMessage
                        id="_.application_progress_bar.yourDecision"
                        defaultMessage="Your decision"
                    />
                );
        }
    };

    if (!appStep) return <></>;

    const renderSteps = () => {
        return steps?.map((step: TStep, index: number) => {
            return (
                <div
                    key={index}
                    className={classNames(
                        styles.status,
                        step.status === 'completed' && styles.completed,
                        step.status === 'active' && styles.active
                    )}
                >
                    <div
                        data-testid={`${_.kebabCase(step.id)}-${step.status}`}
                        className={classNames(styles.circle, {
                            [styles.circleComplete]:
                                step.status === 'completed',
                            [styles.circleActive]: step.status === 'active'
                        })}
                    >
                        {step.status === 'completed' && (
                            <IconCheckCircleFilled
                                data-testid="completed-icon"
                                className={styles.confirmedIcon}
                                aria-hidden
                            />
                        )}
                    </div>
                    <div className={styles.text}>{renderAppStep(step.id)}</div>
                </div>
            );
        });
    };

    return <div className={styles.container}>{renderSteps()}</div>;
};

// eslint-disable-next-line import/no-default-export
export default ApplicationProgressBar;
